import React, { useEffect } from 'react';
// import ReactGA from 'react-ga4';
import { supabase } from '../utils/supabaseClient';

const ThankYou = ({ setStep, formData, setFormData, setRisk, hospitalInfo }) => {
  // コンポーネントのマウント時に自動的にデータを送信
  useEffect(() => {
    const submitFormData = async () => {
      // 既に登録済みの場合はスキップ
      if (formData.isSubmitted) {
        console.log('データは既に送信済みです。重複送信を防止します。');
        return;
      }

      try {
        const sessionId = sessionStorage.getItem('session_id');
        if (!sessionId) {
          console.error('Session ID is missing, cannot submit form data');
          return;
        }

        // 直前の5件のレコードのsession_idを取得
        const { data: lastRecords, error: fetchError } = await supabase
          .from('form_submit')
          .select('session_id')
          .order('created_at', { ascending: false })
          .limit(5);

        if (fetchError) {
          console.error('Error fetching last records:', fetchError);
          return;
        }

        // 直前の5件のレコードが存在し、かつその中に現在のsession_idが存在する場合は登録を中止
        if (lastRecords && lastRecords.length > 0 && lastRecords.some(record => record.session_id === sessionId)) {
          console.log('Duplicate session_id detected, skipping insert.');
          return;
        }

        // targettingがfalseの場合は強制的にnullに設定
        let osteoporosisTreatmentValue = formData.osteoporosisTreatment;
        let boneDensityTestValue = formData.boneDensityTest;
        
        if (hospitalInfo && !hospitalInfo.targetting) {
          console.log('ThankYou: targetting=falseの医療機関のため、事前質問のデータをnullに設定します');
          osteoporosisTreatmentValue = null;
          boneDensityTestValue = null;
        }

        // Supabaseにデータ送信
        const { error } = await supabase
          .from('form_submit')
          .insert([{
            session_id: sessionId,
            complete: false,
            osteoporosis_treatment: osteoporosisTreatmentValue,
            bone_density_test: boneDensityTestValue
          }]);

        if (error) {
          console.error('Supabase form submit error:', error);
          // エラー時はローカルストレージに保存（バックグラウンド同期のために）
          const tempData = {
            session_id: sessionId,
            complete: false,
            osteoporosis_treatment: osteoporosisTreatmentValue,
            bone_density_test: boneDensityTestValue,
            timestamp: new Date().toISOString(),
            original_timestamp: new Date().toISOString() // 元の実行時間を保存
          };
          const storageKey = `temp_thankyou_data_${Date.now()}`;
          localStorage.setItem(storageKey, JSON.stringify(tempData));
          console.log('フォームデータをローカルストレージに保存しました:', storageKey);
          
          // 10秒後にバックグラウンドで再送信処理を試みる
          setTimeout(() => {
            attemptBackgroundSync(storageKey, tempData);
          }, 10000);
        } else {
          console.log('Thank You画面でのデータ送信が成功しました');
          setFormData(prevData => ({ ...prevData, isSubmitted: true }));
        }
      } catch (err) {
        console.error('Thank You画面でのデータ送信エラー:', err);
        // エラー時もユーザーには通知せず内部で処理
        const sessionId = sessionStorage.getItem('session_id');
        if (sessionId) {
          // targettingがfalseの場合は強制的にnullに設定
          let osteoporosisTreatmentValue = formData.osteoporosisTreatment;
          let boneDensityTestValue = formData.boneDensityTest;
          
          if (hospitalInfo && !hospitalInfo.targetting) {
            console.log('ThankYou(エラー時): targetting=falseの医療機関のため、事前質問のデータをnullに設定します');
            osteoporosisTreatmentValue = null;
            boneDensityTestValue = null;
          }
          
          const tempData = {
            session_id: sessionId,
            complete: false,
            osteoporosis_treatment: osteoporosisTreatmentValue,
            bone_density_test: boneDensityTestValue,
            timestamp: new Date().toISOString(),
            original_timestamp: new Date().toISOString() // 元の実行時間を保存
          };
          const storageKey = `temp_thankyou_data_${Date.now()}`;
          localStorage.setItem(storageKey, JSON.stringify(tempData));
          
          // 10秒後にバックグラウンドで再送信処理を試みる
          setTimeout(() => {
            attemptBackgroundSync(storageKey, tempData);
          }, 10000);
        }
      }
    };

    // 自動的にデータを送信
    submitFormData();
  }, [formData, setFormData, hospitalInfo]);

  // バックグラウンド同期処理
  const attemptBackgroundSync = async (storageKey, tempData) => {
    try {
      console.log('一時データを使用して即時再送信を試みます:', storageKey);
      
      // セッションIDを取得
      const sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) {
        console.error('再送信: セッションIDがないため処理を中止します');
        return;
      }
      
      // targettingがfalseの場合は強制的にnullに設定
      let osteoporosisTreatmentValue = tempData.osteoporosis_treatment;
      let boneDensityTestValue = tempData.bone_density_test;
      
      if (hospitalInfo && !hospitalInfo.targetting) {
        console.log('ThankYou(再送信): targetting=falseの医療機関のため、事前質問のデータをnullに設定します');
        osteoporosisTreatmentValue = null;
        boneDensityTestValue = null;
      }
      
      // 即時再送信試行
      const { error } = await supabase
        .from('form_submit')
        .insert([{
          session_id: sessionId,
          complete: false,
          osteoporosis_treatment: osteoporosisTreatmentValue,
          bone_density_test: boneDensityTestValue
        }]);
      
      if (error) {
        console.error('即時再送信エラー:', error);
        // エラーの場合、データはローカルストレージに残り、次回アプリ起動時に処理される
        return;
      }
      
      // 成功したらローカルストレージから削除
      localStorage.removeItem(storageKey);
      console.log('即時再送信成功、一時データを削除しました:', storageKey);
      setFormData(prevData => ({ ...prevData, isSubmitted: true }));
    } catch (err) {
      console.error('即時再送信中の例外:', err);
      // エラーの場合、データはローカルストレージに残り、次回アプリ起動時に処理される
    }
  };

  const handleBackToStart = () => {
    // リセット処理
    sessionStorage.removeItem('session_id');
    sessionStorage.removeItem('has_navigated_to_results');
    
    // フォームデータをリセット
    setFormData({
      weight: null,
      age: null,
      gender: null,
      height: null,
      previousFracture: null,
      parentHipFracture: null,
      currentSmoking: null,
      glucocorticoids: null,
      rheumatoidArthritis: null,
      secondaryOsteoporosis: null,
      alcohol: null,
      dentalCheck: null,
      dentalIssue: null,
      osteoporosisTreatment: null,
      boneDensityTest: null,
      loudSnoring: null,
      fatigue: null,
      observedApnea: null,
      hypertension: null,
      neckCircumference: null,
      isSubmitted: false
    });
    
    if (setRisk) {
      setRisk(null);
    }
    
    setStep(0);
  };

  return (
    <div className="p-4 bg-blue-100 rounded-lg">
      <div className="flex justify-center">
        <div className="flex flex-col items-center text-center space-y-2">
          <h1 className="font-bold text-lg mb-4"><br />あなたはほねなびの対象外のようです。<br /></h1>
          <h2 className="font-bold text-lg mb-4"><br />あなたの身の回りでまだチェックを受けていない方がいらっしゃいましたら<br />ぜひこのチェックをすすめてみてください。<br />この度はご利用ありがとうございました。<br /><br /></h2>
        </div>
      </div>
      <button
        onClick={handleBackToStart}
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
      >
        最初の画面へ戻る
      </button>
    </div>
  );
};

export default ThankYou;